import { adminService } from '@/api/services';
import type { UnwrapData } from '@/api/types';

import type {
  IProgramSectionsResponse,
  ICreateProgramSectionRequest,
  ICreateProgramSectionResponse,
  IDeleteProgramSectionRequest,
  IReorderProgramSectionsRequest,
  IUpdateProgramSectionRequest,
  TUpdateProgramSectionResponse,
} from './types';

export const schoolProgramService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить разделы программы школы
     */
    getSchoolProgramSections: builder.query<UnwrapData<IProgramSectionsResponse>, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/program_sections`,
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolProgramSections', id: schoolId }],
      transformResponse: (response: IProgramSectionsResponse): UnwrapData<IProgramSectionsResponse> => response.data,
    }),
    /**
     * Создать раздел программы школы
     */
    createSchoolProgramSection: builder.mutation<ICreateProgramSectionResponse, ICreateProgramSectionRequest>({
      query: ({ data, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/program_sections`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolProgramSections', id: schoolId }],
    }),
    /**
     * Изменить раздел программы школы
     */
    updateSchoolProgramSection: builder.mutation<TUpdateProgramSectionResponse, IUpdateProgramSectionRequest>({
      query: ({ data, schoolId, programSectionId }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/program_sections/${programSectionId}`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolProgramSections', id: schoolId }],
    }),
    /**
     * Удалить раздел программы школы
     */
    deleteSchoolProgramSection: builder.mutation<void, IDeleteProgramSectionRequest>({
      query: ({ schoolId, programSectionId }) => ({
        method: 'DELETE',
        url: `/schools/${schoolId}/program_sections/${programSectionId}`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolProgramSections', id: schoolId }],
    }),
    /**
     * Изменить порядок разделов программы школы
     */
    reorderSchoolProgramSections: builder.mutation<IProgramSectionsResponse, IReorderProgramSectionsRequest>({
      query: ({ schoolId, data }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/program_sections/reorder`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolProgramSections', id: schoolId }],
    }),
  }),
});

export const {
  useGetSchoolProgramSectionsQuery,
  useCreateSchoolProgramSectionMutation,
  useUpdateSchoolProgramSectionMutation,
  useDeleteSchoolProgramSectionMutation,
  useReorderSchoolProgramSectionsMutation,
} = schoolProgramService;
